import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { Button } from './Button';
import { Input, Textarea } from './Input';
import { FormField } from './FormField';

/**
 * Questions as a real dialog — the text-input sibling of ConfirmDialog.
 * Replaces window.prompt(), which embedded browsers (the desktop app's pane,
 * Electron, some kiosk / locked-down browsers) silently refuse, so the button
 * looks dead. Two shapes:
 *
 *  - TextPromptDialog: one field ("Who took it to the ward?", "Reason for
 *    holding?"). Required unless `optional`; `initialValue` pre-fills it (e.g.
 *    text being corrected).
 *  - FormPromptDialog: a few fields asked together, for a flow that would
 *    otherwise chain prompts (amend reason + corrected text). Never chain
 *    dialogs — one dialog with every field the action needs.
 *
 * Neither closes itself on confirm: the caller runs its action and closes on
 * success, passing `pending`/`error` meanwhile, so a failure is shown in the
 * dialog with the answer still typed in.
 */

export interface PromptField {
  name: string;
  label: string;
  /** Required unless false. */
  required?: boolean;
  initialValue?: string;
  placeholder?: string;
  /** A textarea instead of a one-line input — for free text that can run long. */
  multiline?: boolean;
  /** Defaults to 120 for a one-line field, 2000 for multiline. */
  maxLength?: number;
}

export function FormPromptDialog({ open, title, description, fields, confirmLabel, danger, pending, error, onConfirm, onClose }: {
  open: boolean;
  title: string;
  description?: string;
  fields: PromptField[];
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  /** Server error from the action, shown under the fields. */
  error?: string | null;
  /** Trimmed values, keyed by field name. An empty optional field is ''. */
  onConfirm: (values: Record<string, string>) => void;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);
  // Re-seed only when the dialog opens: `fields` is usually an inline array,
  // so depending on it would wipe what was typed on every parent render.
  useEffect(() => {
    if (open) {
      setValues(Object.fromEntries(fields.map(f => [f.name, f.initialValue ?? ''])));
      setTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isMissing = (f: PromptField) => f.required !== false && !(values[f.name] ?? '').trim();

  const submit = () => {
    setTouched(true);
    if (fields.some(isMissing)) return;
    onConfirm(Object.fromEntries(fields.map(f => [f.name, (values[f.name] ?? '').trim()])));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          {/* pr-6 keeps a long title clear of the dialog's ✕ button. */}
          <DialogTitle className="pr-6">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3">
          {fields.map((f, i) => {
            const common = {
              autoFocus: i === 0,
              value: values[f.name] ?? '',
              placeholder: f.placeholder,
              maxLength: f.maxLength ?? (f.multiline ? 2000 : 120),
            };
            return (
              <FormField key={f.name} label={f.label} required={f.required !== false}
                error={touched && isMissing(f) ? 'Required' : undefined}>
                {f.multiline
                  ? <Textarea {...common} rows={3} onChange={(e) => setValues(v => ({ ...v, [f.name]: e.target.value }))} />
                  : <Input {...common} onChange={(e) => setValues(v => ({ ...v, [f.name]: e.target.value }))} />}
              </FormField>
            );
          })}
          {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant={danger ? 'danger' : 'primary'} loading={pending}>{confirmLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TextPromptDialog({ open, title, description, label, placeholder, initialValue = '', optional, multiline, maxLength, confirmLabel, danger, pending, error, onConfirm, onClose }: {
  open: boolean;
  title: string;
  description?: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  /** Allow an empty answer (onConfirm then receives ''). */
  optional?: boolean;
  multiline?: boolean;
  maxLength?: number;
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  /** Server error from the action, shown under the field. */
  error?: string | null;
  onConfirm: (value: string) => void;
  onClose: () => void;
}) {
  return (
    <FormPromptDialog
      open={open} title={title} description={description}
      fields={[{ name: 'value', label, placeholder, initialValue, required: !optional, multiline, maxLength }]}
      confirmLabel={confirmLabel} danger={danger} pending={pending} error={error}
      onConfirm={(v) => onConfirm(v.value ?? '')} onClose={onClose}
    />
  );
}
